import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  addDays,
  subDays,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
} from 'date-fns';

/**
 * Calculate date range for month view
 * Returns the start of the week containing the first day of the month
 * to the end of the week containing the last day of the month
 */
export function getMonthDateRange(date: Date): { startDate: Date; endDate: Date } {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });
  return { startDate, endDate };
}

/**
 * Calculate date range for week view
 * Returns the start and end of the week containing the given date
 */
export function getWeekDateRange(date: Date): { startDate: Date; endDate: Date } {
  const startDate = startOfWeek(date, { weekStartsOn: 0 });
  const endDate = endOfWeek(date, { weekStartsOn: 0 });
  return { startDate, endDate };
}

/**
 * Calculate date range for day view
 * Returns the start and end of the given day
 */
export function getDayDateRange(date: Date): { startDate: Date; endDate: Date } {
  const startDate = startOfDay(date);
  const endDate = endOfDay(date);
  return { startDate, endDate };
}

/**
 * Get date range based on view type
 */
export function getDateRangeForView(
  date: Date,
  view: 'month' | 'week' | 'day'
): { startDate: Date; endDate: Date } {
  switch (view) {
    case 'month':
      return getMonthDateRange(date);
    case 'week':
      return getWeekDateRange(date);
    case 'day':
      return getDayDateRange(date);
    default:
      return getMonthDateRange(date);
  }
}

/**
 * Navigate to previous period based on view
 */
export function navigatePrevious(date: Date, view: 'month' | 'week' | 'day'): Date {
  switch (view) {
    case 'month':
      return subMonths(date, 1);
    case 'week':
      return subDays(date, 7);
    case 'day':
      return subDays(date, 1);
    default:
      return subMonths(date, 1);
  }
}

/**
 * Navigate to next period based on view
 */
export function navigateNext(date: Date, view: 'month' | 'week' | 'day'): Date {
  switch (view) {
    case 'month':
      return addMonths(date, 1);
    case 'week':
      return addDays(date, 7);
    case 'day':
      return addDays(date, 1);
    default:
      return addMonths(date, 1);
  }
}

/**
 * Check if a date is today
 */
export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

/**
 * Check if a date is in the current month
 */
export function isCurrentMonth(date: Date, currentMonth: Date): boolean {
  return isSameMonth(date, currentMonth);
}

